import { useEffect, Suspense } from 'react';
import { Box } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/navigation/Sidebar';
import { Topbar } from '@/components/navigation/Topbar';
import { tokens } from '@/theme/tokens';
import { useResponsive } from '@/hooks/useResponsive';
import useThemeStore from '@/store/themeStore';
import { Loader } from '@/components/feedback/Loader';
import useAuthStore from '@/store/authStore';
import useSchoolStore from '@/store/schoolStore';
import { buildDynamicNavigation, isValidModule } from '@/utils/dynamicNavigation';

export function DashboardLayout() {
  const { isMobileOrTablet } = useResponsive();
  const collapsed = useThemeStore((s) => s.sidebarCollapsed);
  const location = useLocation();
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const modules = useAuthStore((s) => s.modules);
  const currentSchool = useSchoolStore((s) => s.currentSchool);

  const roleUpper = String(user?.role || '').toUpperCase();
  const isSuperAdmin = roleUpper === 'SUPER_ADMIN';
  const isOrgAdmin = roleUpper === 'ORGANIZATION_ADMIN';

  useEffect(() => {
    if (isSuperAdmin) return;

    let activeMods = [];
    if (isOrgAdmin) {
      const userMods = Array.isArray(modules) ? modules.filter(isValidModule) : [];
      const schoolMods = currentSchool?.school_modules || currentSchool?.modules || [];
      const mappedSchoolMods = Array.isArray(schoolMods)
        ? schoolMods.map((sm) => sm.module || sm).filter(isValidModule)
        : [];
      activeMods = userMods.length > 0 ? userMods : mappedSchoolMods;
    } else {
      activeMods = Array.isArray(modules) ? modules.filter(isValidModule) : [];
    }

    const hasNoAssignment = !currentSchool || activeMods.length === 0;
    const currentPath = location.pathname;

    // If current school has no assigned modules, user can ONLY view root dashboard '/' or '/schools'
    if (hasNoAssignment) {
      if (currentPath !== '/' && currentPath !== '/dashboard' && !currentPath.startsWith('/schools') && !currentPath.startsWith('/super-admin')) {
        navigate('/', { replace: true });
      }
      return;
    }

    // Verify if current route is allowed by active modules
    const navConfig = buildDynamicNavigation(activeMods, user?.role);
    const getRootSegment = (p) => {
      if (!p || p === '#') return '';
      const seg = p.replace(/^\//, '').split('/')[0];
      return seg ? `/${seg}` : '';
    };

    const isAllowedPath = (itemPath) => {
      if (!itemPath || itemPath === '#') return false;
      if (currentPath === itemPath || currentPath.startsWith(itemPath + '/')) return true;
      const itemRoot = getRootSegment(itemPath);
      const currentRoot = getRootSegment(currentPath);
      if (itemRoot && itemRoot === currentRoot) return true;
      return false;
    };

    const hasAccessToCurrentRoute =
      currentPath === '/' ||
      currentPath === '/dashboard' ||
      currentPath.startsWith('/schools') ||
      currentPath.startsWith('/settings') ||
      currentPath.startsWith('/super-admin') ||
      currentPath.startsWith('/academic-sessions') ||
      navConfig.some((item) => {
        if (isAllowedPath(item.path)) return true;
        if (item.children) {
          return item.children.some((child) => isAllowedPath(child.path));
        }
        return false;
      });

    if (!hasAccessToCurrentRoute) {
      const firstAllowed =
        navConfig[0]?.children?.[0]?.path ||
        (navConfig[0]?.path && navConfig[0].path !== '#' ? navConfig[0].path : '/');
      navigate(firstAllowed, { replace: true });
    }
  }, [location.pathname, currentSchool, modules, isSuperAdmin, isOrgAdmin, navigate, user?.role]);

  const sidebarWidth = isMobileOrTablet
    ? 0
    : collapsed
    ? tokens.layout.sidebar.collapsedWidth
    : tokens.layout.sidebar.expandedWidth;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${sidebarWidth}px)`,
          transition: `width ${tokens.transitions.normal}`,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        {/* Topbar */}
        <Topbar />

        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            mt: `${tokens.layout.topbar.height}px`,
            p: { xs: 2, sm: 3 },
            backgroundColor: tokens.colors.background,
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              width: '100%',
            }}
          >
            <Suspense fallback={<Loader message="Loading page..." />}>
              <Outlet />
            </Suspense>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
